import { unref } from 'vue-demi'
import { type MaybeRef, useMediaQuery, computedEager } from '@vueuse/core'
import { type BreakpointConfig, type MediaQueryBreakpointCondition } from '../types'
import { LayoutMode } from '../types/primitives'

export function isMediaQueryBreakpointCondition (obj: unknown): obj is MediaQueryBreakpointCondition {
  return obj != null && typeof obj === 'object' && 'mediaQuery' in obj && typeof obj.mediaQuery === 'string'
}

function getBreakpointActiveRef<TBreakpointCondition> (condition: TBreakpointCondition | undefined, evaluateCondition?: (config: TBreakpointCondition) => MaybeRef<boolean> | undefined): MaybeRef<boolean> {
  if (condition == null) return false
  if (evaluateCondition != null) {
    const isActiveRef = evaluateCondition(condition)
    if (isActiveRef != null) return isActiveRef
  }
  if (isMediaQueryBreakpointCondition(condition)) {
    return useMediaQuery(condition.mediaQuery)
  }
  return false
}

/**
 * Evaluate the current layout mode based on the provided breakpoint config.
 * @param breakpointConfig define the breakpoints and their conditions
 * @param evaluateCondition define a custom function to evaluate the condition
 * @returns the current layout mode
 */
export function useCurrentLayoutMode<TBreakpointCondition> (breakpointConfig?: BreakpointConfig<TBreakpointCondition>, evaluateCondition?: (config: TBreakpointCondition) => MaybeRef<boolean> | undefined) {
  const breakpoints: Array<{ key: string, isActiveRef: MaybeRef<boolean> }> = (breakpointConfig?.order ?? []).map((key) => {
    const condition = breakpointConfig?.modes[key]
    return {
      key,
      isActiveRef: getBreakpointActiveRef(condition, evaluateCondition)
    }
  })
  const currentMode = computedEager((): LayoutMode | undefined => {
    const firstActive = breakpoints.find((item) => unref(item.isActiveRef))
    if (firstActive == null) return undefined
    return firstActive.key
  })
  return {
    currentMode
  }
}
